import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { reduce } from 'lodash';
import moment from 'moment';

import { RegionService } from '../../../core/api/region.service';
import { RoleEnum } from '../../../shared/constants/role.enums';
import { Region, User, YieldCurve } from '../../../shared/models';
import { ToastLevel } from '../../../shared/models/toast-message.module';
import { ToastService } from '../../../shared/services/toast.service';
import * as fromApp from '../../../store';
import { clearRegionYieldCurveContent } from '../../../store/admin/admin.actions';
import { selectRegionUpload, selectYieldCurveUpload } from '../../../store/admin/admin.selectors';

@Component({
  selector: 'fc-upload-region',
  templateUrl: './upload-region.component.html',
  styleUrls: ['./upload-region.component.scss'],
})
export class UploadRegionComponent implements OnInit {
  @ViewChild('regionFileUpload', { static: false })
  regionFileUpload: ElementRef;
  @ViewChild('yieldCurveFileUpload', { static: false })
  yieldCurveFileUpload: ElementRef;

  destroyRef = inject(DestroyRef);
  regionForm: UntypedFormGroup;
  kmlFileName = null;
  kmlGeometry = null;
  csvFileName = null;
  csvYieldCurve = null;
  errorMessage = null;
  showForm = true;
  id: number = null;
  region: Region = null;
  currentUser: User = null;
  roles = RoleEnum;
  versionedYieldCurves: YieldCurve[][];

  constructor(
    private formBuilder: UntypedFormBuilder,
    readonly regionService: RegionService,
    readonly toastService: ToastService,
    readonly store: Store<fromApp.AppState>,
    readonly router: Router,
    readonly route: ActivatedRoute
  ) {
    this.createNewRegionForm();
    this.id = +this.route.snapshot.params.id;

    if (this.id) {
      this.showForm = false;
      this.regionService.getRegion(this.id).subscribe(region => {
        if (region) {
          this.region = region;

          let yieldCurves = reduce(
            this.region.yieldCurves,
            (result, value, key) => {
              (result[value.version] || (result[value.version] = [])).push(value);
              return result;
            },
            {}
          );
          this.versionedYieldCurves = Object.keys(yieldCurves)
            .reverse()
            .map(v => yieldCurves[v]);
          this.createUpdateRegionForm();
        }
      });
    }
  }

  ngOnInit() {
    this.store
      .select('auth', 'currentUser')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(currentUser => (this.currentUser = currentUser));

    this.store
      .select(selectRegionUpload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(regionUpload => {
        this.kmlFileName = regionUpload.fileName;
        this.kmlGeometry = regionUpload.kmlGeoJSON;
        this.kmlGeometry
          ? this.regionForm.get('regionFileLoaded').setValue('Loaded')
          : this.regionForm.get('regionFileLoaded').setValue('');
      });

    this.store
      .select(selectYieldCurveUpload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(yieldUpload => {
        this.csvFileName = yieldUpload.fileName;
        this.csvYieldCurve = yieldUpload.yieldCurveData;
        this.csvYieldCurve
          ? this.regionForm.get('yieldCurveFileLoaded').setValue('Loaded')
          : this.regionForm.get('yieldCurveFileLoaded').setValue('');
      });
  }

  createNewRegionForm(): void {
    this.regionForm = this.formBuilder.group({
      name: ['', Validators.required],
      refreshDate: [null, Validators.required],
      regionFileLoaded: ['', Validators.required],
      yieldCurveFileLoaded: ['', Validators.required],
    });
  }

  createUpdateRegionForm(): void {
    this.regionForm = this.formBuilder.group({
      name: [this.region.name, Validators.required],
      refreshDate: [moment(this.region.refreshDate, true).toDate(), Validators.required],
      regionFileLoaded: [''],
      yieldCurveFileLoaded: [''],
    });
  }

  onClickEdit(): void {
    this.regionForm.controls.name.setValue(this.region.name);
    this.regionForm.controls.refreshDate.setValue(moment(this.region.refreshDate, true).toDate());
    this.showForm = true;
  }

  onClickKmlUpload(): void {
    const regionFileUpload = this.regionFileUpload.nativeElement;
    regionFileUpload.onchange = () => {
      if (regionFileUpload.files.length === 1) {
        const file = regionFileUpload.files[0];
        this.uploadKmlFile(file);
        this.regionFileUpload.nativeElement.value = '';
      }
    };
    regionFileUpload.click();
  }

  onClickYieldCurveUpload(): void {
    const yieldCurveFileUpload = this.yieldCurveFileUpload.nativeElement;
    yieldCurveFileUpload.onchange = () => {
      if (yieldCurveFileUpload.files.length === 1) {
        const file = yieldCurveFileUpload.files[0];
        this.uploadCsvFile(file);
        this.yieldCurveFileUpload.nativeElement.value = '';
      }
    };
    yieldCurveFileUpload.click();
  }

  uploadKmlFile = (file: any) => {
    const fileExtension = file.name.substr(file.name.lastIndexOf('.') + 1);
    if (fileExtension == 'kml') {
      this.regionService.parseKmlDocument(file).then(parsedKml => {});
    } else if (fileExtension == 'kmz') {
      this.regionService.parseKmzDocument(file).then(parsedKml => {});
    } else {
      throw new Error('Only support .kml and .kmz file format');
    }
  };

  uploadCsvFile = (file: any) => {
    const fileExtension = file.name.substr(file.name.lastIndexOf('.') + 1);
    if (fileExtension == 'csv') {
      this.regionService.parseCsvDocument(file).then(parsedKml => {});
    } else {
      throw new Error('Only support .csv file format');
    }
  };

  onSubmit(): void {
    this.errorMessage = null;

    this.regionService
      .createRegion(
        this.regionForm.get('name').value,
        moment(this.regionForm.get('refreshDate').value, true).format('YYYY-MM-DD'),
        this.kmlGeometry,
        this.csvYieldCurve
      )
      .subscribe(
        result => {
          this.errorMessage = null;
          this.regionForm.reset();
          this.store.dispatch(clearRegionYieldCurveContent());
          this.router.navigate(['/admin/manage/region']);
        },
        (err: HttpErrorResponse) => {
          this.errorMessage = err.message;
        },
        () => {}
      );
  }

  onSubmitUpdate(): void {
    this.errorMessage = null;

    const updateRegion = new Region({
      id: this.region.id,
      name: this.regionForm.get('name').value,
      refreshDate: moment(this.regionForm.get('refreshDate').value, true).format('YYYY-MM-DD'),
    });

    if (this.kmlGeometry) {
      updateRegion.boundary = this.kmlGeometry;
    }
    if (this.csvYieldCurve) {
      updateRegion.yieldCurves = this.csvYieldCurve;
    }

    this.regionService.updateRegion(updateRegion).subscribe(
      updatedRegion => {
        this.regionForm.reset();
        this.toastService.sendMessage('This region has been updated successfully.', ToastLevel.SUCCESS);
        this.router.navigate(['/admin/manage/region']);
      },
      (err: HttpErrorResponse) => {
        this.errorMessage = err.message;
      },
      () => {}
    );
  }

  disableUploadButton(): boolean {
    return (
      this.region.name === this.regionForm.get('name').value &&
      this.region.refreshDate === this.regionForm.get('refreshDate').value &&
      this.regionForm.get('regionFileLoaded').value.length === 0 &&
      this.regionForm.get('yieldCurveFileLoaded').value.length === 0
    );
  }

  get regionRefreshDate(): string {
    return moment(this.region.refreshDate, true).format('MM/DD/YYYY');
  }

  get isFormDirty(): boolean {
    return this.regionForm.dirty;
  }

  getErrorMessage(formControl: string, displayName: string): string {
    if (this.regionForm.touched && this.regionForm.get(formControl).hasError('required')) {
      return `${displayName} is required`;
    }
  }
}
